import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { Loader2, Instagram, Youtube, Music2, Target, Pencil, Check, Upload, Mail, Send } from "lucide-react";
import api from "../lib/apiClient";
import { XpBar } from "../components/XpBar";
import { BADGE_ICONS } from "../lib/gameData";
import { toast } from "sonner";

export default function Profile() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [avatar, setAvatar] = useState("");
  const [tz, setTz] = useState("UTC");
  const [recap, setRecap] = useState(true);
  const [nudge, setNudge] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/profile").then((r) => {
    setData(r.data);
    setAvatar(r.data.profile?.avatar_url || "");
    setTz(r.data.profile?.timezone || "UTC");
    setRecap(r.data.profile?.email_weekly_recap !== false);
    setNudge(r.data.profile?.email_nudge !== false);
  });
  useEffect(() => { load(); }, []);

  const saveTz = async () => {
    await api.put("/profile", { timezone: tz });
    toast.success("Fuseau horaire mis à jour");
    load();
  };

  const togglePref = async (key, value, setter) => {
    setter(value);
    try {
      await api.put("/profile", { [key]: value });
      toast.success("Préférence enregistrée");
    } catch {
      setter(!value);
      toast.error("Échec de l'enregistrement");
    }
  };

  const sendRecapNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/emails/weekly-recap/me");
      toast[data.sent ? "success" : "error"](data.sent ? "Bilan envoyé ! Regarde ta boîte mail 📬" : "Envoi indisponible (réessaie dans un instant).");
    } catch {
      toast.error("Échec de l'envoi.");
    } finally {
      setSending(false);
    }
  };


  const saveAvatar = async () => {
    await api.put("/profile", { avatar_url: avatar });
    toast.success("Photo mise à jour");
    setEditing(false);
    load();
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/profile", { avatar_url: data.url });
      toast.success("Photo importée !");
      setEditing(false);
      load();
    } catch {
      toast.error("Échec de l'import (image de 5 Mo max).");
    }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const { user, profile, level_info: lvl, stats, badges, artist_score, streak, career_title } = data;
  const earnedCount = badges.filter((b) => b.earned).length;

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-extrabold">Profil</h1>

      <MesEspaces />
      <MonAbonnement />
      <MonProfilPublic />
      <MesManagers />
      <MesAdmin />

      {/* Identity card */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 relative grain" data-testid="profile-card">
        <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
          <div className="relative">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-amber-500/40 bg-zinc-900 flex items-center justify-center">
              {avatar ? <img src={avatar} alt="avatar" className="w-full h-full object-cover" /> :
                <span className="font-display text-4xl font-extrabold gold-text">{(user.artist_name || "A")[0].toUpperCase()}</span>}
            </div>
            <button onClick={() => setEditing((v) => !v)} data-testid="edit-avatar-btn"
              className="absolute -bottom-2 -right-2 w-8 h-8 rounded-lg gold-gradient text-black flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl font-extrabold" data-testid="profile-name">{user.artist_name}</h2>
            <p className="text-sm text-zinc-400 mt-0.5">{profile?.genre} · {profile?.current_level}</p>
            <div className="inline-flex items-center gap-2 mt-3 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30">
              <span className="font-mono text-xs text-amber-400">Niveau {lvl.level} — {career_title}</span>
            </div>
          </div>
        </div>
        {editing && (
          <div className="relative z-10 mt-5 space-y-2">
            <label className="flex items-center justify-center gap-2 h-11 rounded-xl bg-zinc-900 border border-dashed border-zinc-700 cursor-pointer hover:border-amber-500 text-sm text-zinc-300" data-testid="avatar-upload-label">
              <Upload className="w-4 h-4 text-amber-400" /> Importer une photo (max 5 Mo)
              <input type="file" accept="image/*" className="hidden" data-testid="avatar-upload-input"
                onChange={(e) => uploadAvatar(e.target.files?.[0])} />
            </label>
            <div className="flex gap-2">
              <input value={avatar} onChange={(e) => setAvatar(e.target.value)} data-testid="avatar-url-input" placeholder="…ou colle une URL d'image"
                className="flex-1 h-11 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none" />
              <button onClick={saveAvatar} data-testid="save-avatar-btn" className="px-4 h-11 rounded-xl gold-gradient text-black font-semibold inline-flex items-center gap-1.5"><Check className="w-4 h-4" /> Ok</button>
            </div>
          </div>
        )}
        <div className="relative z-10 mt-5">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-mono text-amber-400">{user.total_xp} XP</span>
            <span className="text-zinc-500">{lvl.is_max ? "Niveau max" : `Prochain : ${lvl.next_threshold} XP`}</span>
          </div>
          <XpBar value={user.total_xp - lvl.current_threshold} max={lvl.is_max ? 1 : lvl.xp_for_next_level} />
        </div>
      </div>

      {/* Goal */}
      {profile?.main_goal && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="profile-goal">
          <div className="flex items-center gap-2 text-amber-500 mb-2"><Target className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">Objectif</span></div>
          <p className="font-display text-lg font-bold">{profile.main_goal}</p>
          {profile.goal_description && <p className="text-sm text-zinc-400 mt-1">{profile.goal_description}</p>}
        </div>
      )}

      {/* Timezone setting */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="profile-timezone">
        <div className="flex items-center gap-2 text-amber-500 mb-3"><Target className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">Fuseau horaire</span></div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select value={tz} onChange={(e) => setTz(e.target.value)} data-testid="timezone-input"
            className="flex-1 h-11 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none">
            {[...new Set([tz, "Europe/Paris", "Europe/London", "Europe/Brussels", "Europe/Madrid", "Europe/Zurich",
              "America/New_York", "America/Los_Angeles", "America/Montreal", "America/Sao_Paulo",
              "Africa/Casablanca", "Africa/Abidjan", "Asia/Dubai", "Asia/Tokyo", "Australia/Sydney", "UTC"])].map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
          <button onClick={saveTz} data-testid="save-timezone-btn" className="px-4 h-11 rounded-xl gold-gradient text-black font-semibold">Enregistrer</button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">Toutes tes dates et heures sont affichées dans ce fuseau (format IANA, ex : Europe/Paris, America/New_York).</p>
      </div>

      {/* Email notifications */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="profile-email-prefs">
        <div className="flex items-center gap-2 text-amber-500 mb-4"><Mail className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">Notifications e-mail</span></div>
        <Toggle
          testid="toggle-weekly-recap"
          label="Bilan hebdomadaire"
          desc="Chaque lundi : tes tâches finies, XP, Score de progression, priorités + ton objectif de la semaine."
          checked={recap}
          onChange={(v) => togglePref("email_weekly_recap", v, setRecap)}
        />
        <div className="h-px bg-zinc-800 my-4" />
        <Toggle
          testid="toggle-nudge"
          label="Relance douce"
          desc="Un rappel bienveillant si tu restes inactif plus de 14 jours, pour reprendre l'élan."
          checked={nudge}
          onChange={(v) => togglePref("email_nudge", v, setNudge)}
        />
        <button onClick={sendRecapNow} disabled={sending} data-testid="send-recap-now-btn"
          className="mt-5 w-full h-11 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 text-sm text-zinc-200 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 text-amber-400" />}
          {sending ? "Envoi…" : "M'envoyer mon bilan maintenant"}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Box label="Score de progression" value={`${artist_score}`} testid="profile-score" />
        <Box label="Streak 🔥" value={`${streak} j`} testid="profile-streak" />
        <Box label="XP total" value={user.total_xp} testid="profile-xp" />
        <Box label="Niveau" value={lvl.level} testid="profile-level" />
        <Box label="Projets" value={stats.projects_total} testid="profile-projects" />
        <Box label="Tâches finies" value={stats.tasks_done} testid="profile-tasks" />
      </div>

      {/* Social links */}
      {(profile?.instagram || profile?.tiktok || profile?.youtube || profile?.spotify) && (
        <div className="flex flex-wrap gap-3" data-testid="profile-socials">
          {profile.instagram && <Social icon={Instagram} label="Instagram" href={profile.instagram} />}
          {profile.tiktok && <Social icon={Music2} label="TikTok" href={profile.tiktok} />}
          {profile.youtube && <Social icon={Youtube} label="YouTube" href={profile.youtube} />}
          {profile.spotify && <Social icon={Music2} label="Spotify" href={profile.spotify} />}
        </div>
      )}

      {/* Badges */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Badges</h2>
          <span className="font-mono text-sm text-amber-400">{earnedCount}/{badges.length}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="badges-grid">
          {badges.map((b) => {
            const Icon = BADGE_ICONS[b.icon] || BADGE_ICONS.default;
            return (
              <div key={b.code} data-testid={`badge-${b.code}`}
                className={`rounded-2xl p-4 border text-center ${b.earned ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-[#131318] opacity-60"}`}>
                <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2 ${b.earned ? "gold-gradient text-black glow-gold" : "bg-zinc-800 text-zinc-600"}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <p className={`text-xs font-medium ${b.earned ? "text-zinc-100" : "text-zinc-500"}`}>{b.name}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5 leading-tight">{b.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <p className="text-sm font-semibold text-zinc-100">{label}</p>
        <p className="text-xs text-zinc-500 mt-0.5 leading-snug">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testid}
        onClick={() => onChange(!checked)}
        className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${checked ? "gold-gradient" : "bg-zinc-700"}`}>
        <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${checked ? "left-6" : "left-1"}`} />
      </button>
    </div>
  );
}

function MesAdmin() {
  const { identity } = useAuth();
  const navigate = useNavigate();
  if (!identity?.is_admin) return null;
  return (
    <button onClick={() => navigate("/admin")} data-testid="go-admin"
      className="w-full h-12 rounded-2xl border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm font-semibold inline-flex items-center justify-center gap-2">
      🛡️ Console d'administration
    </button>
  );
}

function MesManagers() {
  const [managers, setManagers] = useState(null);
  const PERM_LABELS = {
    view_projects: "Voir mes projets", view_progress: "Voir ma progression",
    view_statistics: "Voir mes statistiques", validate_projects: "Valider mes projets/missions",
    manage_tasks: "Gérer mes tâches", manage_goals: "Gérer mes objectifs",
  };
  const [grantable, setGrantable] = useState([]);
  const load = () => api.get("/artist/managers").then((r) => { setManagers(r.data.managers); setGrantable(r.data.grantable); }).catch(() => setManagers([]));
  useEffect(() => { load(); }, []);

  const toggle = async (mid, perm, current) => {
    const has = current.includes(perm);
    const next = has ? current.filter((p) => p !== perm) : [...current, perm];
    setManagers((ms) => ms.map((m) => m.manager_id === mid ? { ...m, permissions: ["view_profile", ...next.filter((p) => p !== "view_profile")] } : m));
    try { await api.put(`/artist/managers/${mid}/permissions`, { permissions: next }); toast.success("Autorisations mises à jour"); }
    catch { toast.error("Échec"); load(); }
  };

  if (!managers || managers.length === 0) return null;
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="mes-managers">
      <h2 className="font-display text-lg font-bold mb-1">Mes managers</h2>
      <p className="text-zinc-500 text-sm mb-4">Choisis ce que chaque manager peut voir ou faire. Tu restes propriétaire de tes données.</p>
      <div className="space-y-4">
        {managers.map((m) => (
          <div key={m.manager_id} className="rounded-2xl border border-zinc-800 bg-[#0F0F13] p-4" data-testid={`manager-perms-${m.manager_id}`}>
            <p className="font-semibold text-zinc-100 mb-3">🧑‍💼 {m.name}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {grantable.map((perm) => {
                const on = m.permissions.includes(perm);
                return (
                  <button key={perm} onClick={() => toggle(m.manager_id, perm, m.permissions.filter((p) => p !== "view_profile"))}
                    data-testid={`perm-${m.manager_id}-${perm}`}
                    className={`flex items-center justify-between gap-2 px-3 h-11 rounded-xl border text-sm text-left ${on ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-zinc-800 text-zinc-400"}`}>
                    <span>{PERM_LABELS[perm] || perm}</span>
                    <span className={`w-4 h-4 rounded-full shrink-0 ${on ? "bg-amber-400" : "bg-zinc-700"}`} />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonProfilPublic() {
  const navigate = useNavigate();
  const [vis, setVis] = useState("");
  const [options, setOptions] = useState([]);
  useEffect(() => {
    api.get("/me/visibility").then((r) => { setVis(r.data.visibility); setOptions(r.data.options || []); }).catch(() => {});
  }, []);
  const save = async (v) => {
    setVis(v);
    try { await api.put("/me/visibility", { visibility: v }); toast.success("Visibilité mise à jour"); }
    catch { toast.error("Échec"); }
  };
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="mon-profil-public">
      <h2 className="font-display text-lg font-bold mb-1">Visibilité & Marché</h2>
      <p className="text-zinc-500 text-sm mb-4">Choisis comment tu apparais sur Cehnura et propose tes services.</p>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5">Ma visibilité</label>
      <select value={vis} onChange={(e) => save(e.target.value)} data-testid="visibility-select"
        className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-200 mb-4">
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      <div className="grid sm:grid-cols-2 gap-3">
        <button onClick={() => navigate("/marketplace")} data-testid="go-marketplace"
          className="h-11 rounded-xl bg-zinc-900 border border-zinc-700 hover:border-amber-500/50 text-sm text-zinc-200">🔎 Explorer le Marché</button>
        <button onClick={() => navigate("/pro")} data-testid="go-pro-setup"
          className="h-11 rounded-xl gold-gradient text-black font-semibold text-sm">💼 Proposer mes services</button>
      </div>
    </div>
  );
}

function MonAbonnement() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  useEffect(() => { api.get("/subscription").then((r) => setSub(r.data)).catch(() => {}); }, []);
  const plan = sub?.plan || "FREE";
  const isManager = (sub?.profile?.active_space || "CREATOR") === "MANAGER";
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="mon-abonnement">
      <h2 className="font-display text-lg font-bold mb-1">Abonnement</h2>
      <p className="text-zinc-500 text-sm mb-4">
        {isManager
          ? "Ton espace Manager est gratuit."
          : <>Offre actuelle : <span className="text-amber-400 font-semibold" data-testid="profile-current-plan">{plan}</span>.</>}
      </p>
      <button onClick={() => navigate("/subscription")} data-testid="go-subscription"
        className="w-full h-11 rounded-xl gold-gradient text-black font-semibold text-sm inline-flex items-center justify-center gap-2">
        <Crown className="w-4 h-4" /> {(plan === "FREE" && !isManager) ? "Voir les offres & passer Premium" : "Gérer mon abonnement"}
      </button>
    </div>
  );
}

function MesEspaces() {
  const { identity, switchSpace } = useAuth();
  const navigate = useNavigate();
  if (!identity) return null;
  const isMgr = identity.roles?.includes("MANAGER");
  const plan = identity.artist?.plan || "FREE";
  const go = async (space) => { await switchSpace(space); navigate(space === "MANAGER" ? "/manager" : "/dashboard"); };
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="mes-espaces">
      <h2 className="font-display text-lg font-bold mb-4">Mes espaces</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-zinc-800 bg-[#0F0F13] p-4 flex flex-col" data-testid="space-artist-card">
          <div className="flex items-center gap-2 mb-1"><span className="text-lg">✦</span><span className="font-semibold">Mon espace</span></div>
          <p className="text-xs text-zinc-500 mb-3">Offre : <span className="text-amber-400 font-semibold">{plan}</span></p>
          <button onClick={() => go("CREATOR")} data-testid="go-artist-space" className="mt-auto h-10 rounded-xl bg-zinc-900 border border-zinc-700 hover:border-amber-500/50 text-sm text-zinc-200">Accéder à mon espace</button>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-[#0F0F13] p-4 flex flex-col" data-testid="space-manager-card">
          <div className="flex items-center gap-2 mb-1"><span className="text-lg">🧑‍💼</span><span className="font-semibold">Espace Manager</span></div>
          <p className="text-xs text-zinc-500 mb-3">{isMgr ? <span className="text-emerald-400 font-semibold">Actif</span> : "Non activé"}</p>
          {isMgr
            ? <button onClick={() => go("MANAGER")} data-testid="go-manager-space" className="mt-auto h-10 rounded-xl gold-gradient text-black font-semibold text-sm">Accéder à mon espace</button>
            : <button onClick={() => navigate("/become-manager")} data-testid="become-manager-link" className="mt-auto h-10 rounded-xl gold-gradient text-black font-semibold text-sm">Devenir Manager</button>}
        </div>
      </div>
    </div>
  );
}

function Box({ label, value, testid }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover" data-testid={testid}>
      <p className="font-display text-2xl font-extrabold">{value}</p>
      <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
    </div>
  );
}

function Social({ icon: Icon, label, href }) {
  const url = href.startsWith("http") ? href : `https://${href.replace(/^@/, "")}`;
  return (
    <a href={url} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-[#18181E] border border-zinc-800 text-sm text-zinc-300 hover:border-amber-500/50 hover:text-amber-400 transition-colors">
      <Icon className="w-4 h-4" /> {label}
    </a>
  );
}
